import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [cartSource, setCartSource] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    const savedSource = localStorage.getItem('cartSource');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to load cart', e);
      }
    }
    if (savedSource) {
      try {
        setCartSource(JSON.parse(savedSource));
      } catch (e) {
        console.error('Failed to load cart source', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
    localStorage.setItem('cartSource', JSON.stringify(cartSource));
  }, [cartItems, cartSource]);

  const addToCart = (product, quantity = 1, source, sourceName) => {
    // Check if cart has a different source
    if (cartSource && source && cartSource.id !== source.id) {
      toast.error(`Cannot add product from different vendor. Your cart already contains items from another vendor.`);
      return false;
    }

    // Set the cart source if not already set
    if (!cartSource && source) {
      setCartSource({ 
        ...source, 
        name: sourceName || (source.type === 'hub' ? 'Hub' : 'Producer'),
        slug: source.slug || null
      });
    }

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.productId === product._id);
      if (existingItem) {
        return prevItems.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevItems, {
        productId: product._id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        quantity: quantity,
        images: product.images,
        slug: product.slug
      }];
    });

    toast.success(`Added ${product.name} to cart`);
    return true;
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.productId !== productId));
    if (cartItems.length === 1) {
      setCartSource(null);
    }
    toast.success('Item removed from cart');
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.productId === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = (silent = false) => {
    setCartItems([]);
    setCartSource(null);
    localStorage.removeItem('cart');
    localStorage.removeItem('cartSource');
    if (!silent) {
      toast.success('Cart cleared');
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartSource = () => cartSource;

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    cartSource,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartSource,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
